import { useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SearchInput } from '@/types/job.types';
import {
  getSkillSuggestions,
  getRoleSuggestions,
  getSearchLocationSuggestions,
} from '@/services/api/homeApi';
import { Toast } from './Toast';
import styles from './JobSearchBar.module.css';

/**
 * JobSearchBar — search input for skills/roles + location.
 * Mirrors Flutter JobSearchBar widget + JobSearchBarBloc with reference UI design.
 * switchIndex: 0 = skills, 1 = roles
 *
 * - Skills/roles are typed comma-separated; the token under the cursor is
 *   autocompleted from /skill/_suggest or /role/_suggest. Go/Enter turns the
 *   typed text into chips and runs the search. The merged chip list is capped at
 *   7 skills / 5 roles with Flutter's error (JobSearchBarBloc._OnClickGo).
 * - Location autocompletes from /location/_suggest (city, min 3 chars).
 *   Selecting or pressing Enter locks it in as a chip and runs the search.
 */

const MAX_SKILLS_COUNT = 7;
const MAX_ROLES_COUNT = 5;
const SUGGEST_DEBOUNCE_MS = 500;
const ONLY_PUNCTUATION = /^[ -/:-@[-`{-~]+$/;

interface JobSearchBarProps {
  onSearch: (input: SearchInput) => void;
  isMobile?: boolean;
  searchInput?: SearchInput;
  /** 'inline' drops the card chrome so the bar sits inside a page's own search card. */
  variant?: 'default' | 'inline';
  /** Skill/Role switch — Flutter _buildRoleAndSkillToggle shows it on every page. */
  showSwitch?: boolean;
  /** Controlled skill chips — lets the page add skills (e.g. from an extracted resume). */
  skills?: string[];
  onSkillsChange?: (skills: string[]) => void;
}

function splitList(value?: string): string[] {
  return (value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

/** Mirrors Flutter getUniqueData: drops items an earlier item already starts with. */
function getUniqueData(list: string[]): string[] {
  const unique: string[] = [];
  for (const item of list) {
    const needle = item.toLowerCase().trim();
    if (!unique.some((u) => u.toLowerCase().trim().startsWith(needle))) {
      unique.push(item);
    }
  }
  return unique;
}

export function JobSearchBar({
  onSearch,
  isMobile = false,
  searchInput,
  variant = 'default',
  showSwitch,
  skills: controlledSkills,
  onSkillsChange,
}: JobSearchBarProps) {
  const isInline = variant === 'inline';
  const hasSwitch = showSwitch ?? true;
  const { t } = useTranslation();
  const [switchIndex, setSwitchIndex] = useState(searchInput?.switchIndex ?? 0);
  const [skillText, setSkillText] = useState('');
  const [roleText, setRoleText] = useState('');
  const [ownSkillChips, setOwnSkillChips] = useState<string[]>(
    searchInput?.listOfSearchSkills ?? splitList(searchInput?.skills),
  );
  const skillChips = controlledSkills ?? ownSkillChips;
  const setSkillChips = useCallback(
    (next: string[]) => {
      if (controlledSkills === undefined) setOwnSkillChips(next);
      onSkillsChange?.(next);
    },
    [controlledSkills, onSkillsChange],
  );
  const [roleChips, setRoleChips] = useState<string[]>(
    searchInput?.listOfSearchRoles ?? splitList(searchInput?.roles),
  );
  const [locationText, setLocationText] = useState('');
  const [locationChip, setLocationChip] = useState(searchInput?.locations ?? '');
  const [limitError, setLimitError] = useState('');

  const tokenRange = useRef({ start: 0, end: 0 });

  const isSkills = switchIndex === 0;
  const termText = isSkills ? skillText : roleText;
  const setTermText = isSkills ? setSkillText : setRoleText;
  const chips = isSkills ? skillChips : roleChips;

  const submit = useCallback(
    (location = locationChip) => {
      let skills = [...skillChips];
      let roles = [...roleChips];

      if (switchIndex === 0 && skillText.trim()) {
        for (const s of splitList(skillText)) {
          if (!skills.includes(s)) skills.push(s);
        }
      } else if (switchIndex === 1 && roleText.trim()) {
        roles.push(...new Set(splitList(roleText)));
      }
      // Capped on the merged chip list: in Flutter, chips added by the page (resume skills on
      // /search_by_resume) sit in the typed text until Go, so they count toward the limit too
      if (switchIndex === 0 && skills.length > MAX_SKILLS_COUNT) {
        skills = skills.slice(0, MAX_SKILLS_COUNT);
        setLimitError(`You can enter maximum ${MAX_SKILLS_COUNT} skills.`);
      } else if (switchIndex === 1 && roles.length > MAX_ROLES_COUNT) {
        roles = roles.slice(0, MAX_ROLES_COUNT);
        setLimitError(`You can enter maximum ${MAX_ROLES_COUNT} roles.`);
      }

      skills = getUniqueData(skills);
      roles = getUniqueData(roles);
      setSkillChips(skills);
      setRoleChips(roles);
      setSkillText('');
      setRoleText('');

      onSearch({
        switchIndex,
        skills: skills.join(','),
        roles: roles.join(','),
        ...(location ? { locations: location } : {}),
        listOfSearchSkills: skills,
        listOfSearchRoles: roles,
      });
    },
    [skillChips, roleChips, skillText, roleText, switchIndex, locationChip, onSearch, setSkillChips],
  );

  // ── Skill / role token autocomplete (Flutter SearchBarContainer.suggestionsCallback) ──
  const getTermQuery = useCallback((text: string, cursor: number): string | null => {
    if (!text.trim()) return null;
    const prevChar = text[cursor > 0 ? cursor - 1 : 0];
    if (prevChar === ',' || prevChar === ' ') return null;

    const start = text.slice(0, cursor).lastIndexOf(',') + 1;
    const nextComma = text.indexOf(',', cursor);
    const end = nextComma >= 0 ? nextComma : text.length;
    tokenRange.current = { start, end };

    const typed = text.slice(start, end).trim();
    return typed && !ONLY_PUNCTUATION.test(typed) ? typed : null;
  }, []);

  const fetchTermPage = useCallback(
    (query: string, page: number) =>
      switchIndex === 0 ? getSkillSuggestions(query, page) : getRoleSuggestions(query, page),
    [switchIndex],
  );

  const selectTerm = useCallback(
    (value: string) => {
      const { start, end } = tokenRange.current;
      setTermText((text) => `${text.slice(0, start)}${value}${text.slice(end)}`);
    },
    [setTermText],
  );

  const removeChip = (index: number) => {
    const setChips = isSkills ? setSkillChips : setRoleChips;
    setChips(chips.filter((_, i) => i !== index));
  };

  // ── Location autocomplete (Flutter _buildLocationSearchField) ──
  const getLocationQuery = useCallback((text: string): string | null => {
    const typed = text.trim();
    if (text.length < 3 || !typed || ONLY_PUNCTUATION.test(typed)) return null;
    return typed;
  }, []);

  const commitLocation = useCallback(
    (value: string) => {
      const loc = value.trim();
      if (!loc) {
        submit();
        return;
      }
      setLocationChip(loc);
      setLocationText(loc);
      submit(loc);
    },
    [submit],
  );

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Flutter ignores an un-committed location on Go; keep the typed text instead of dropping it.
    if (!locationChip && locationText.trim()) {
      commitLocation(locationText);
    } else {
      submit();
    }
  };

  const clearLocation = () => {
    setLocationChip('');
    setLocationText('');
  };

  const clearLimitError = useCallback(() => setLimitError(''), []);

  return (
    <form
      className={`${styles.bar} ${isMobile ? styles.barMobile : ''} ${isInline ? styles.barInline : ''}`}
      onSubmit={handleFormSubmit}
    >
      {/* Switch: Skill / Role — Flutter _buildRoleAndSkillToggle */}
      {hasSwitch && (
        <div className={styles.switchRow} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={switchIndex === 0}
            className={`${styles.switchBtn} ${switchIndex === 0 ? styles.switchActive : ''}`}
            onClick={() => setSwitchIndex(0)}
          >
            {t('Skill')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={switchIndex === 1}
            className={`${styles.switchBtn} ${switchIndex === 1 ? styles.switchActive : ''}`}
            onClick={() => setSwitchIndex(1)}
          >
            {t('Role')}
          </button>
        </div>
      )}

      {/* Search input */}
      <div className={styles.inputGroup}>
        <TypeaheadInput
          key={switchIndex}
          value={termText}
          placeholder={isSkills ? t('Enter skills') : t('Enter Job role')}
          icon={
            <svg className={styles.icon} width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="9" cy="9" r="6" />
              <path d="M13.5 13.5 17 17" />
            </svg>
          }
          chips={chips.map((chip, i) => (
            <Chip key={`${chip}-${i}`} label={chip} onRemove={() => removeChip(i)} />
          ))}
          getQuery={getTermQuery}
          fetchPage={fetchTermPage}
          onChange={setTermText}
          onSelect={selectTerm}
          onEnter={() => submit()}
        />

        {locationChip ? (
          <div className={`${styles.inputWrapper} ${styles.locationField}`}>
            <LocationIcon />
            <div className={styles.chips}>
              <Chip label={locationChip} onRemove={clearLocation} />
            </div>
          </div>
        ) : (
          <TypeaheadInput
            className={styles.locationField}
            value={locationText}
            placeholder={t('Enter Location')}
            icon={<LocationIcon />}
            getQuery={getLocationQuery}
            fetchPage={getSearchLocationSuggestions}
            onChange={setLocationText}
            onSelect={commitLocation}
            onEnter={() => commitLocation(locationText)}
          />
        )}

        <button type="submit" className={styles.searchBtn}>
          {t('Go')}
        </button>
      </div>

      {limitError && <Toast message={limitError} type="error" onClose={clearLimitError} />}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LocationIcon() {
  return (
    <svg className={styles.icon} width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round">
      <path d="M10 17s5.5-5 5.5-9a5.5 5.5 0 0 0-11 0c0 4 5.5 9 5.5 9Z" />
      <circle cx="10" cy="8" r="2" />
    </svg>
  );
}

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className={styles.chip} title={label}>
      <span className={styles.chipLabel}>{label}</span>
      <button type="button" className={styles.chipRemove} onClick={onRemove} aria-label={`Remove ${label}`}>
        ×
      </button>
    </span>
  );
}

interface TypeaheadInputProps {
  className?: string;
  value: string;
  placeholder: string;
  icon: ReactNode;
  chips?: ReactNode[];
  /** Returns the text to query suggestions for, or null to hide suggestions. */
  getQuery: (value: string, cursor: number) => string | null;
  fetchPage: (query: string, page: number) => Promise<string[]>;
  onChange: (value: string) => void;
  onSelect: (suggestion: string) => void;
  onEnter: () => void;
}

/**
 * Debounced, paginated typeahead — mirrors Flutter PrimarySearchableTextField
 * (500ms debounce, next page loaded when the list is scrolled to the bottom).
 */
function TypeaheadInput({
  className = '',
  value,
  placeholder,
  icon,
  chips,
  getQuery,
  fetchPage,
  onChange,
  onSelect,
  onEnter,
}: TypeaheadInputProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const requestIdRef = useRef(0);
  const queryRef = useRef('');
  const pageRef = useRef(1);
  const exhaustedRef = useRef(false);
  const loadingRef = useRef(false);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const close = () => {
    requestIdRef.current += 1;
    clearTimeout(debounceRef.current);
    setOpen(false);
    setActiveIndex(-1);
  };

  const load = useCallback(
    async (query: string, page: number) => {
      const requestId = ++requestIdRef.current;
      loadingRef.current = true;
      try {
        const data = await fetchPage(query, page);
        if (requestId !== requestIdRef.current) return;
        if (data.length === 0) exhaustedRef.current = true;
        setSuggestions((prev) => (page === 1 ? data : [...prev, ...data]));
        if (page === 1) {
          setActiveIndex(-1);
          setOpen(data.length > 0);
        }
      } catch {
        if (requestId === requestIdRef.current && page === 1) setOpen(false);
      } finally {
        if (requestId === requestIdRef.current) loadingRef.current = false;
      }
    },
    [fetchPage],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    const cursor = e.target.selectionStart ?? text.length;
    onChange(text);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const query = getQuery(text, cursor);
      if (query == null) {
        close();
        return;
      }
      queryRef.current = query;
      pageRef.current = 1;
      exhaustedRef.current = false;
      load(query, 1);
    }, SUGGEST_DEBOUNCE_MS);
  };

  const handleListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 8) return;
    if (loadingRef.current || exhaustedRef.current) return;
    pageRef.current += 1;
    load(queryRef.current, pageRef.current);
  };

  const choose = (suggestion: string) => {
    close();
    onSelect(suggestion);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) el.setSelectionRange(el.value.length, el.value.length);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && open) {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp' && open) {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      close();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && activeIndex >= 0) {
        choose(suggestions[activeIndex]);
      } else {
        close();
        onEnter();
      }
    }
  };

  return (
    <div className={`${styles.inputWrapper} ${styles.typeahead} ${className}`}>
      {icon}
      {chips && chips.length > 0 && <div className={styles.chips}>{chips}</div>}
      <input
        ref={inputRef}
        type="text"
        className={styles.input}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={close}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && suggestions.length > 0 && (
        <ul
          className={styles.suggestions}
          role="listbox"
          onScroll={handleListScroll}
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.map((s, i) => (
            <li
              key={`${s}-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              className={`${styles.suggestion} ${i === activeIndex ? styles.suggestionActive : ''}`}
              onClick={() => choose(s)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
